import { SEARCH_SERVICE_PROVIDERS } from '@/constants/grs';
import { getCategoryAttributes } from '@/src/components/SearchResultsFacets/grs.lib';
import {
    FacetBucketNormalized,
    FacetNormalized,
} from '@/src/components/SearchResultsFacets/types';
import {
    GRS_SEARCH_PROVIDER_SESSION_STORAGE_VALUE,
    SEARCH_PROVIDER_SESSION_STORAGE_KEY,
} from '@/src/constants/grs';
import { GRSCategoryProps } from '@/src/types/grs';
import { createUUID } from '@nxt-costco-com/forge-digital-components';

export const getIsGRSSearchProviderSessionStorageSet = () => {
    const isGRSSearchProviderSessionStorageSet =
        sessionStorage
            .getItem(SEARCH_PROVIDER_SESSION_STORAGE_KEY)
            ?.toLowerCase() === GRS_SEARCH_PROVIDER_SESSION_STORAGE_VALUE;

    return isGRSSearchProviderSessionStorageSet;
};

export const setGRSSearchProviderSessionStorage = () => {
    return sessionStorage.setItem(
        SEARCH_PROVIDER_SESSION_STORAGE_KEY,
        GRS_SEARCH_PROVIDER_SESSION_STORAGE_VALUE,
    );
};

export const getAdobeSessionId = ({
    searchService,
    adobeSessionIdLocalForage,
    hasLocalForageError,
    isLocalForageInitialzed,
}: {
    searchService:
        | (typeof SEARCH_SERVICE_PROVIDERS)[keyof typeof SEARCH_SERVICE_PROVIDERS]
        | null;
    adobeSessionIdLocalForage: string | null;
    hasLocalForageError: boolean;
    isLocalForageInitialzed: boolean;
}) => {
    if (searchService !== SEARCH_SERVICE_PROVIDERS.GRS) {
        // If search service is not GRS, return null to prevent unnecessary search api calls
        return null;
    } else if (!hasLocalForageError && !isLocalForageInitialzed) {
        // Return null to prevent returning new UUID before localforage reads value in indexedDB
        return null;
    } else if (adobeSessionIdLocalForage) {
        return adobeSessionIdLocalForage;
    } else {
        return createUUID();
    }
};

const sortCategories = (a: GRSCategoryProps, b: GRSCategoryProps) => {
    // First, sort by level
    if (a.level && b.level) {
        const levelDiff = a.level - b.level;
        if (levelDiff !== 0) {
            return levelDiff;
        }
    }

    // If levels are equal (or missing), sort alphabetically by label
    if (a.label && b.label) {
        return a.label.localeCompare(b.label);
    }

    return 0;
};

export const checkCurrentCategory = (
    bucket: FacetBucketNormalized,
    categoryId: string | undefined,
) => {
    if (!categoryId) {
        return false;
    }
    return categoryId && bucket.val?.toString()?.startsWith(categoryId);
};

export const getFilteredCategories = (
    facet: FacetNormalized,
    categoryId?: string,
    requireSelectionInHeirarchy?: boolean,
) => {
    const filteredCategories: GRSCategoryProps[] = [];
    const selectedCategory = facet.buckets.find(bucket =>
        checkCurrentCategory(bucket, categoryId),
    );
    const selectedCategoryAttributes =
        selectedCategory &&
        getCategoryAttributes(selectedCategory?.val.toString());

    facet.buckets.forEach(bucket => {
        if (!bucket.val) {
            return;
        }

        const categoryAttributes = getCategoryAttributes(
            bucket?.val.toString(),
        );

        const category: GRSCategoryProps = {
            ...bucket,
            ...categoryAttributes,
            isSelectedCateogry:
                categoryAttributes.id === selectedCategoryAttributes?.id,
        };

        //  Push all L1 categories if no category is selected
        if (
            !requireSelectionInHeirarchy &&
            (!selectedCategoryAttributes || !categoryId) &&
            category.level &&
            category.level === 1
        ) {
            filteredCategories.push(category);
        }

        // Push the selected L1 category and it's associated L2 categories
        if (
            selectedCategoryAttributes?.level === 1 &&
            (category.id === selectedCategoryAttributes?.id ||
                category.parent === selectedCategoryAttributes?.id)
        ) {
            filteredCategories.push(category);
        }

        // Push the selected L2 and it's assoicated L1 Category and L3 Categories
        if (
            selectedCategoryAttributes?.level === 2 &&
            (category.id === selectedCategoryAttributes?.id ||
                (category.level === 1 &&
                    category.id === selectedCategoryAttributes?.parent) ||
                (category.level === 3 &&
                    category.parent === selectedCategoryAttributes?.id))
        ) {
            filteredCategories.push(category);
        }

        // Push the selected L3 category and it's parent L1 and L2 categories and L4 categories
        if (
            selectedCategoryAttributes?.level === 3 &&
            (category.id === selectedCategoryAttributes?.id ||
                (category.level === 2 &&
                    category.id === selectedCategoryAttributes?.parent) ||
                (category.level === 1 &&
                    category.id === selectedCategoryAttributes.l1cateogry) ||
                (category.level === 4 &&
                    category.parent === selectedCategoryAttributes?.id))
        ) {
            filteredCategories.push(category);
        }

        // Push the selected L4 category and it's parent L1, L2, and L3 categories
        if (
            selectedCategoryAttributes?.level === 4 &&
            (category.id === selectedCategoryAttributes?.id ||
                (category.level === 3 &&
                    category.id === selectedCategoryAttributes?.parent) ||
                (category.level === 2 &&
                    category.id === selectedCategoryAttributes.l2cateogry) ||
                (category.level === 1 &&
                    category.id === selectedCategoryAttributes.l1cateogry))
        ) {
            filteredCategories.push(category);
        }
    });

    const sortedCategories = filteredCategories.sort(sortCategories);

    return sortedCategories;
};
